import { Box, Flex, useToast } from '@chakra-ui/react';
import { format, fromUnixTime } from 'date-fns';
import { Transaction } from 'metaverse-js/lib/proto/model/wallet';
import React, { memo, useEffect, useState } from 'react';
import { shortenAddress } from '../../../lib/shortenAddress';
import TableUI from '../../UI/TableUI';
import styled from '@emotion/styled';
import { formatTransactionState } from '../../../lib';
import { Link } from 'react-router-dom';
import { formatCurrency, getEnv } from '../../../utils';
import { SCAN_URL_CONFIG } from '../../../config';
import CopyIcon from '../../Icons/CopyIcon';

export const Status = styled.div<{ status: TransactionState }>(
	(props) => `
	text-transform: capitalize;
    color: ${
			props.status === 'COMPLETED'
				? '#21FF0E'
				: props.status === 'FAILED' ||
				  props.status === 'REJECTED' ||
				  props.status === 'INVALID'
				? '#FF0000'
				: props.status === 'PROCESSING'
				? '#FFA500'
				: '#737283'
		}
`
);

interface DataTable {
	[key: string]: any;
	currency: string;
	time: string;
	amount: string;
	withdraw_address: string;
	blockchain_transaction_id: string;
	status: string;
	onchainHash: string;
}

const WithdrawHistory = ({ transactions }: { transactions: Transaction[] }) => {
	const [dataTable, setDataTable] = useState<DataTable[]>([]);
	const toast = useToast();
	const columns: Columns[] = [
		{
			key: 'currency',
			title: 'Currency',
		},
		{
			key: 'chain',
			title: 'Network',
		},
		{
			key: 'time',
			title: 'Time',
		},
		{
			key: 'amount',
			title: 'Amount',
			render: (amount: string) => <>{formatCurrency(amount)}</>,
		},
		{
			key: 'withdraw_address',
			title: 'Withdraw Address',
			render: (withdraw_address: string) => (
				<Flex alignItems={'center'} gap={'10px'}>
					{shortenAddress(withdraw_address)}
					<CopyIcon
						style={{ cursor: 'pointer' }}
						onClick={() => handleCopy(withdraw_address)}
					/>
				</Flex>
			),
		},
		{
			key: 'fromWallet',
			title: 'From Wallet',
		},
		{
			key: 'toWallet',
			title: 'To Wallet',
		},
		{
			key: 'onchainHash',
			title: 'Blockchain Transaction Hash',
			render: (onchainHash: string, record: any) => (
				<>
					{onchainHash && (
						<Link
							style={{ color: 'blue' }}
							to={`${SCAN_URL_CONFIG[record.chain]}${onchainHash}`}
							target="_blank"
						>
							{shortenAddress(onchainHash)}
						</Link>
					)}
				</>
			),
		},
		{
			key: 'status',
			title: 'Status',
			isNumeric: true,
			render: (status: TransactionState) => (
				<Status status={status}>{formatTransactionState(status)}</Status>
			),
		},
	] as Columns[];
	useEffect(() => {
		const newData = transactions.map((item) => ({
			currency: item.assetSymbol || '',
			time: item.createdAt
				? format(fromUnixTime(item.createdAt), 'yyyy-MM-dd HH:mm a')
				: '',
			amount: item.assetAmount || '',
			withdraw_address: item.toAccount || '',
			blockchain_transaction_id: shortenAddress(item.id) || '',
			status: item.state || '',
			onchainHash: item.onchainHash || '',
			fromWallet: item.fromChain || '',
			toWallet: item.toChain || '',
			chain: item.toChain || '',
		}));
		setDataTable(newData);
	}, [transactions]);

	const handleCopy = (text: string | undefined) => {
		if (!text) return;
		navigator.clipboard.writeText(text);
		toast({
			title: 'Copied!',
			status: 'info',
			duration: 2000,
			isClosable: true,
			position: 'top',
		});
	};

	return (
		<>
			<Box display={{ base: 'none', md: 'block' }}>
				<TableUI isBorder={true} columns={columns} dataTable={dataTable} />
			</Box>

			<Box display={{ base: 'block', md: 'none' }} overflow={'auto'}>
				{dataTable.map((data, index) => {
					return (
						<Flex
							justifyContent={'space-between'}
							key={index}
							borderTop={'1px solid #FFFFFF1E'}
							p={'20px'}
						>
							<Box>
								{columns.slice(0, 4).map((col) => (
									<Flex key={col.key} alignItems={'center'} mb={'10px'}>
										<>
											<Box w={'90px'} color={'#737283'} fontSize={'12px'}>
												{col.title}:{' '}
											</Box>
											<Box fontSize={'14px'} w={'90px'}>
												{data[col.key]}
											</Box>
										</>
									</Flex>
								))}
							</Box>
							<Box>
								{columns.slice(4, columns.length).map((col) => (
									<Flex key={col.key} mb={'10px'} alignItems={'flex-start'}>
										<Box w={'70px'} color={'#737283'} fontSize={'12px'}>
											{col.title}:
										</Box>
										<Box fontSize={'14px'} w={'90px'}>
											{col.key === 'status' ? (
												col.render && col.render(data[col.key])
											) : col.key === 'onchainHash' ? (
												<Link
													style={{ color: 'blue' }}
													to={`${SCAN_URL_CONFIG[data.chain || '']}${
														data[col.key]
													}`}
													target="_blank"
												>
													{shortenAddress(data[col.key])}
												</Link>
											) : col.key === 'withdraw_address' ? (
												<Flex
													alignItems={'center'}
													gap={'10px'}
													flexWrap={'wrap'}
												>
													{shortenAddress(data[col.key])}
													<CopyIcon
														style={{ cursor: 'pointer' }}
														onClick={() => handleCopy(data[col.key])}
													/>
												</Flex>
											) : (
												data[col.key]
											)}
										</Box>
									</Flex>
								))}
							</Box>
						</Flex>
					);
				})}
			</Box>
		</>
	);
};

export default memo(WithdrawHistory);
